import React, { useEffect, useState } from "react";
import { X, Upload } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchProviderPriority,
  fetchCountry,
  addOrUpdateProviderImage,
} from "../../redux/slices/ProvidergalSlice";

const AddServiceImageModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [isImageUpload, setIsImageUpload] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const { providerPriolist, countryList } = useSelector(
    (state) => state.providerGallery
  );

  const [form, setForm] = useState({
    provider_image_gallery_id: "",
    country_id: "",
    name: "",
    priority: "",
    image: null,
  });

  // Fetch dropdown data when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchProviderPriority());
      dispatch(fetchCountry());
    }
  }, [isOpen, dispatch]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setForm({ ...form, [name]: files[0] });
      setIsImageUpload(true);
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.country_id || !form.priority) {
      Swal.fire("Error", "Please fill all required fields", "warning");
      return;
    }

    const formData = new FormData();

    if (form.provider_image_gallery_id) {
      formData.append(
        "provider_image_gallery_id",
        form.provider_image_gallery_id
      );
    }

    formData.append("country_id", form.country_id);
    formData.append("name", form.name);
    formData.append("priority", form.priority);

    if (form.image) formData.append("image", form.image);

    setIsAdding(true);

    try {
      await dispatch(addOrUpdateProviderImage(formData)).unwrap();

      Swal.fire("Success", "Image saved successfully", "success");
      onClose();

      setForm({
        provider_image_gallery_id: "",
        country_id: "",
        name: "",
        priority: "",
        image: null,
      });
    } catch (err) {
      Swal.fire("Error", err.message || "Something went wrong", "error");
    }

    setIsAdding(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"></div>

      {/* Modal */}
      <div className="fixed inset-0 flex justify-center items-center z-50 px-4">
        <div className="bg-white w-full max-w-2xl rounded-xl shadow-lg">
          {/* Header */}
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-800">
              {form.provider_image_gallery_id
                ? "Edit Service Image"
                : "Add New Service Image"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={22} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Priority */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Select Priority
              </label>
              <select
                name="priority"
                value={form.priority}
                onChange={handleChange}
                className="p-3 border border-gray-300 rounded-md bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300 transition-all cursor-pointer"
              >
                <option value="">Select Priority</option>
                {providerPriolist?.map((priority, index) => (
                  <option key={index} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>

            {/* Country */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Country Selection
              </label>
              <select
                name="country_id"
                value={form.country_id}
                onChange={handleChange}
                className="p-3 border border-gray-300 rounded-md bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300 transition-all cursor-pointer"
              >
                <option value="">Select country</option>
                {countryList?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.country_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Image Name or Number
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. 'Main Service Shot' or '001'"
                className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 transition-all"
              />
            </div>
            {/* Upload Box */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center flex flex-col justify-center items-center hover:border-orange-400 transition relative">
              <Upload size={40} className="text-gray-400 mb-3" />
              <input
                type="file"
                name="image"
                className="hidden"
                onChange={handleChange}
                id="serviceImage"
              />
              {isImageUpload ? (
                <label
                  htmlFor="serviceImage"
                  className="text-sm text-orange-500 font-medium cursor-pointer hover:underline"
                >
                  Click to Change
                </label>
              ) : (
                <label
                  htmlFor="serviceImage"
                  className="text-sm text-orange-500 font-medium cursor-pointer hover:underline"
                >
                  Click to upload
                </label>
              )}
              {form.image && (
                <p className="mt-2 text-orange-500 text-bold text-xs">
                  {form.image.name}
                </p>
              )}
              {/* <p className="text-sm text-gray-600 mt-1">or drag and drop</p>
              <p className="text-xs text-gray-400 mt-2">
                Recommended size: 1000×1000px, max 5MB
              </p> */}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t px-6 py-4">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isAdding}
              className="px-6 py-2 rounded-md bg-orange-500 text-white flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {isAdding ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddServiceImageModal;
